import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as supplierService from "../../services/supplierService";
import * as hrmsService from "../../services/hrmsService";
import { ChatPanel } from "../Proc/Chat";
import { DataTable, ProcBadge, amount, fileSize, linkBtn, openSigned } from "../Proc/procUi";
import { DetailGrid, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, cardStyle, formatDate, formatDateTime, inputStyle, pageStyle, useLoad } from "../../components/ui";

// One RFQ for the supplier: what the buyer wants, and the quotation form.

const HOW = {
  LUMPSUM: "The buyer will give the whole RFQ to one supplier, so price every item.",
  ITEM: "The buyer can give each item to a different supplier, so you can price only the items you can supply.",
  SPLIT: "The buyer can share an item between suppliers, so you can price some items and offer part of the quantity.",
};
const total = (rows) => rows.reduce((sum, r) => sum + (Number(r.price) || 0) * (Number(r.quantity) || 0), 0);

function QuoteForm({ rfq, reload, onClose }) {
  const q = rfq.quotation;
  const split = rfq.rfq_type === "SPLIT";
  const [head, setHead] = useState({
    delivery_time: q ? q.delivery_time || "" : "",
    quotation_validity: q ? q.quotation_validity || "" : "",
    payment_terms: q ? q.payment_terms || "" : "",
    additional_info: q ? q.additional_info || "" : "",
  });
  const [rows, setRows] = useState(() =>
    Object.fromEntries(
      rfq.lines.map((l) => {
        const p = q && q.prices.find((x) => x.line_no === l.line_no);
        return [l.line_no, { price: p ? p.price : "", quantity: p ? p.quantity : l.quantity, remarks: p && p.remarks ? p.remarks : "" }];
      })
    )
  );
  const [busy, setBusy] = useState(false);
  const setRow = (no, patch) => setRows((r) => ({ ...r, [no]: { ...r[no], ...patch } }));

  const priced = rfq.lines.filter((l) => rows[l.line_no].price !== "" && rows[l.line_no].price !== null);
  const sum = total(priced.map((l) => ({ price: rows[l.line_no].price, quantity: split ? rows[l.line_no].quantity : l.quantity })));

  const upload = async (need, e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const res = await supplierService.uploadQuotationDocument(rfq.id, need.id, file);
    if (res?.success) {
      toast.success("Document uploaded");
      reload();
    } else toast.error(hrmsService.responseMessage(res));
  };
  const removeDoc = async (answer) => {
    try {
      const res = await supplierService.removeQuotationDocument(rfq.id, answer.id);
      if (res?.success === false) toast.error(hrmsService.responseMessage(res));
      else reload();
    } catch (err) {
      toast.error(err.message || "The document could not be removed");
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await supplierService.sendQuotation(rfq.id, {
        ...head,
        lines: rfq.lines.map((l) => ({ line_id: l.id, price: rows[l.line_no].price, ...(split ? { quantity: rows[l.line_no].quantity } : {}), remarks: rows[l.line_no].remarks })),
      });
      if (res?.success) {
        toast.success(res.message || "Quotation sent");
        onClose();
        reload();
      } else toast.error(hrmsService.responseMessage(res));
    } catch (err) {
      toast.error(err.message || "The quotation could not be sent");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ ...cardStyle, padding: 16, marginBottom: 20 }}>
      <h6>{q ? "Revise your quotation" : "Your quotation"}</h6>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>
        Prices are per unit in {rfq.currency}. {HOW[rfq.rfq_type]}
      </div>
      {rfq.lines.map((l) => (
        <div key={l.line_no} style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div className="d-flex flex-wrap gap-2 align-items-baseline mb-2">
            <strong>{l.line_no}. {l.item}</strong>
            <span style={{ color: "#6B7280", fontSize: 13 }}>{l.quantity} {l.uom || ""} wanted</span>
            <span className="ms-auto" style={{ fontSize: 13 }}>
              {rows[l.line_no].price === "" ? "Not quoting" : `${rfq.currency} ${amount((Number(rows[l.line_no].price) || 0) * (split ? Number(rows[l.line_no].quantity) || 0 : l.quantity))}`}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0 12px" }}>
            <Field label="Unit price" htmlFor={`sq-price-${l.line_no}`}>
              <input id={`sq-price-${l.line_no}`} type="number" min={0} className="form-control" style={inputStyle} value={rows[l.line_no].price} onChange={(e) => setRow(l.line_no, { price: e.target.value })} />
            </Field>
            {split ? (
              <Field label="Quantity you offer" htmlFor={`sq-qty-${l.line_no}`}>
                <input id={`sq-qty-${l.line_no}`} type="number" min={0} max={l.quantity} className="form-control" style={inputStyle} value={rows[l.line_no].quantity} onChange={(e) => setRow(l.line_no, { quantity: e.target.value })} />
              </Field>
            ) : null}
            <Field label="Remarks" htmlFor={`sq-rem-${l.line_no}`}>
              <input id={`sq-rem-${l.line_no}`} className="form-control" style={inputStyle} maxLength={500} value={rows[l.line_no].remarks} onChange={(e) => setRow(l.line_no, { remarks: e.target.value })} />
            </Field>
          </div>
        </div>
      ))}
      <div className="text-end mb-3" style={{ fontSize: 16 }}>
        Total <strong>{rfq.currency} {amount(sum)}</strong>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0 16px" }}>
        <Field label="Delivery time" required htmlFor="sq-delivery" hint="For example: 5 working days">
          <input id="sq-delivery" className="form-control" style={inputStyle} maxLength={100} value={head.delivery_time} onChange={(e) => setHead({ ...head, delivery_time: e.target.value })} />
        </Field>
        <Field label="Prices valid until" required htmlFor="sq-validity" hint={rfq.responsible_date ? `Not before ${formatDate(rfq.responsible_date)}` : undefined}>
          <input id="sq-validity" type="date" className="form-control" style={inputStyle} value={head.quotation_validity} onChange={(e) => setHead({ ...head, quotation_validity: e.target.value })} />
        </Field>
        <Field label="Payment terms" htmlFor="sq-terms">
          <input id="sq-terms" className="form-control" style={inputStyle} maxLength={200} value={head.payment_terms} onChange={(e) => setHead({ ...head, payment_terms: e.target.value })} />
        </Field>
      </div>
      <Field label="Additional information" htmlFor="sq-info">
        <textarea id="sq-info" className="form-control" rows={2} maxLength={1000} value={head.additional_info} onChange={(e) => setHead({ ...head, additional_info: e.target.value })} />
      </Field>

      {rfq.required_documents.length ? (
        <>
          <h6 className="mt-3">Documents the buyer asked for</h6>
          {rfq.required_documents.map((n) => (
            <div key={n.id} className="mb-3" style={{ fontSize: 14 }}>
              <div>
                <strong>{n.document}</strong> {n.is_mandatory ? <span style={{ color: "#B91C1C" }}>(required)</span> : null}
                {n.rfq_param ? <span style={{ color: "#6B7280" }}> · {n.rfq_param}</span> : null}
              </div>
              {n.answers.map((a) => (
                <div key={a.id} className="d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                  <button type="button" style={linkBtn} onClick={() => openSigned(() => supplierService.getQuotationDocumentUrl(rfq.id, a.id), toast)}>{a.file_name}</button>
                  <span style={{ color: "#6B7280" }}>{fileSize(a.size)}</span>
                  <button type="button" className="btn btn-link text-danger p-0" style={{ fontSize: 13 }} onClick={() => removeDoc(a)}>Remove</button>
                </div>
              ))}
              <label className="btn btn-outline-secondary btn-sm mt-1 mb-0">
                Upload a file
                <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={(e) => upload(n, e)} aria-label={`Upload ${n.document}`} />
              </label>
            </div>
          ))}
        </>
      ) : null}

      <div className="d-flex gap-2 justify-content-end">
        <SecondaryButton onClick={onClose} disabled={busy}>Cancel</SecondaryButton>
        <PrimaryButton onClick={submit} disabled={busy}>{q ? "Send revised quotation" : "Send quotation"}</PrimaryButton>
      </div>
    </div>
  );
}

export default function SupplierRfqDetail() {
  const { id } = useParams();
  const { data: rfq, loading, error, reload } = useLoad(() => supplierService.getRfq(id), [id]);
  const [quoting, setQuoting] = useState(false);
  const [asking, setAsking] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [chatting, setChatting] = useState(false);

  const q = rfq && rfq.quotation;
  return (
    <div style={pageStyle}>
      <PageHeader
        title={rfq ? rfq.title : "RFQ"}
        subtitle={rfq ? `${rfq.rfq_number} · ${rfq.buyer_name}` : ""}
        actions={
          rfq ? (
            <span className="d-flex flex-wrap gap-2 align-items-center">
              <ProcBadge status={rfq.state} label={rfq.state_label} />
              {rfq.can_quote && !quoting ? <PrimaryButton onClick={() => setQuoting(true)}>{q ? "Revise quotation" : "Send quotation"}</PrimaryButton> : null}
              {rfq.can_quote || rfq.can_decline ? <SecondaryButton onClick={() => setAsking(true)}>Ask a question</SecondaryButton> : null}
              <SecondaryButton onClick={() => setChatting(true)}>Message the buyer</SecondaryButton>
              {rfq.can_decline ? <SecondaryButton onClick={() => setDeclining(true)}>Decline</SecondaryButton> : null}
            </span>
          ) : null
        }
      />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rfq ? (
          <>
            {quoting ? <QuoteForm rfq={rfq} reload={reload} onClose={() => setQuoting(false)} /> : null}
            <div style={{ ...cardStyle, padding: 16 }} className="mb-4">
              <DetailGrid
                columns={3}
                items={[
                  ["Buyer", rfq.buyer_name],
                  ["Quote by", formatDate(rfq.responsible_date)],
                  ["Delivery date", rfq.delivery_date ? formatDate(rfq.delivery_date) : null],
                  ["Delivery location", rfq.location],
                  ["How it is awarded", HOW[rfq.rfq_type]],
                  ["Instructions", rfq.instructions],
                ]}
              />
              {rfq.info_request ? <div className="mt-3" style={{ fontSize: 13 }}><strong>Your question:</strong> {rfq.info_request}</div> : null}
              {rfq.declined_at ? <div className="mt-3" style={{ fontSize: 13 }}>You declined this RFQ on {formatDateTime(rfq.declined_at)}.</div> : null}
              {rfq.state === "REJECTED" && q && q.review_note ? <div className="mt-3" style={{ fontSize: 13, color: "#991B1B" }}>The buyer did not accept your quotation: {q.review_note}</div> : null}
            </div>

            {rfq.orders && rfq.orders.length ? (
              <div style={{ ...cardStyle, padding: 12, background: "#ECFDF5" }} className="mb-4">
                You were awarded: {rfq.orders.map((o) => <Link key={o.id} to={`/supplier/orders/${o.id}`} className="me-3">Order {o.order_number}</Link>)}
              </div>
            ) : null}
            <h6>What the buyer needs</h6>
            <DataTable
              minWidth={560}
              rowKey={(l) => l.line_no}
              rows={rfq.lines}
              columns={[
                { label: "#", render: (l) => l.line_no, width: 50 },
                { label: "Item", render: (l) => <><div style={{ fontWeight: 600 }}>{l.item}</div><div style={{ fontSize: 12, color: "#6B7280" }}>{[l.category_name, l.brand].filter(Boolean).join(" · ")}</div></> },
                { label: "Quantity", render: (l) => `${l.quantity} ${l.uom || ""}` },
                { label: "Specification", render: (l) => l.description || "—" },
                {
                  label: "Files",
                  render: (l) => {
                    const mine = rfq.attachments.filter((f) => f.owner_type === "RFQ_LINE_ATTACHMENT" && f.line_no === l.line_no);
                    return mine.length ? mine.map((f) => <div key={f.id}><button type="button" style={linkBtn} onClick={() => openSigned(() => supplierService.getRfqAttachmentUrl(id, f.id), toast)}>{f.file_name}</button></div>) : "—";
                  },
                },
              ]}
            />
            {rfq.attachments.some((f) => f.owner_type === "RFQ_ATTACHMENT") ? (
              <div className="mt-3" style={{ fontSize: 14 }}>
                <strong>Files from the buyer: </strong>
                {rfq.attachments.filter((f) => f.owner_type === "RFQ_ATTACHMENT").map((f) => (
                  <span key={f.id} className="me-3">
                    <button type="button" style={linkBtn} onClick={() => openSigned(() => supplierService.getRfqAttachmentUrl(id, f.id), toast)}>{f.file_name}</button>
                  </span>
                ))}
              </div>
            ) : null}

            {q && !quoting ? (
              <>
                <h6 className="mt-4">Your quotation {q.quotation_number}{q.revision > 1 ? ` · revision ${q.revision}` : ""}</h6>
                <DataTable
                  minWidth={480}
                  rowKey={(p) => p.line_no}
                  rows={q.prices}
                  columns={[
                    { label: "Item", render: (p) => (rfq.lines.find((l) => l.line_no === p.line_no) || {}).item },
                    { label: "Quantity", render: (p) => p.quantity },
                    { label: "Unit price", render: (p) => amount(p.price) },
                    { label: "Amount", render: (p) => amount(p.price * p.quantity) },
                    { label: "Remarks", render: (p) => p.remarks || "—" },
                  ]}
                  footer={<tr><th colSpan={3} style={{ padding: "10px 12px" }}>Total ({q.currency})</th><th colSpan={2} style={{ padding: "10px 12px" }}>{amount(q.total)}</th></tr>}
                />
                <div style={{ fontSize: 13, color: "#374151" }} className="mt-2">
                  Delivery: {q.delivery_time} · Valid until {formatDate(q.quotation_validity)}{q.payment_terms ? ` · Payment: ${q.payment_terms}` : ""} · Sent {formatDateTime(q.submitted_at)}
                </div>
              </>
            ) : null}
            {!q && !rfq.can_quote && !rfq.declined_at ? <EmptyState title="This RFQ is no longer open" text="The deadline has passed or the buyer closed it." /> : null}
          </>
        ) : null}
      </LoadState>
      {chatting && rfq ? <ChatPanel service={supplierService} refQuery={{ buyer_id: rfq.buyer_id, rfq_id: rfq.id }} title={rfq.buyer_name} subtitle={`${rfq.rfq_number} · ${rfq.title}`} onClose={() => setChatting(false)} /> : null}
      <FormDrawer
        show={asking}
        title="Ask the buyer a question"
        submitLabel="Send question"
        initial={{}}
        onHide={() => setAsking(false)}
        onSave={(clean) => supplierService.requestRfqInfo(id, clean.note)}
        onDone={reload}
        fields={[{ key: "note", label: "Your question", type: "textarea", required: true, max: 1000, hint: "The buyer is notified and can see it on the RFQ" }]}
      />
      <FormDrawer
        show={declining}
        title="Decline this RFQ"
        submitLabel="Confirm decline"
        initial={{}}
        onHide={() => setDeclining(false)}
        onSave={(clean) => supplierService.declineRfq(id, clean.reason)}
        onDone={reload}
        fields={[{ key: "reason", label: "Reason (optional)", type: "textarea", max: 500, hint: "Tells the buyer why you will not quote" }]}
      />
    </div>
  );
}
